import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BarData, DataNode } from "../../interface/Trends";
import {
  TrendCurveChartData,
  TrendItem,
} from "../../interface/DesignerInterface";

interface ColorAnalysisDataL2 {
  [key: string]: DataNode[];
}

interface L2Data {
  [colorSelectedForL1: string]: DataNode;
}

export interface TrendCurveCache {
  filterType: string;
  curveWindowType: string;
  req_id: string;
  colorContextKey: string;
  chartData: TrendCurveChartData;
  rawData: TrendItem[];
}

export interface TrendCurvePreferences {
  filterType: string;
  curveWindowType: string;
  req_id: string;
}

interface TrendData {
  req_id: string;
  overallAnalysisData: null | BarData;
  colorAnalysisDataL1: null | DataNode;
  l2Data: L2Data;
  trendCurveCache: TrendCurveCache | null;
  trendCurvePreferences: TrendCurvePreferences | null;
  /** Bumps on each analysis run so trend curves refetch even when req_id is unchanged. */
  trendCurveRefreshKey: number;
}

interface TrendsState {
  trendsData: TrendData | null;
  graphRevision: number;
  /** L1 colour picked in UI; applied to trend-curve fetches until Run Analysis persists session. */
  pendingColorForLevel2: string | null;
  /** Names (e.g. "Trend 1") of the trends archived for the current analysis. */
  archivedTrends: string[];
}

const initialState: TrendsState = {
  trendsData: null,
  graphRevision: 0,
  pendingColorForLevel2: null,
  archivedTrends: [],
};

const trendSlice = createSlice({
  name: "trends",
  initialState,
  reducers: {
    setTrendsData: (state, action: PayloadAction<TrendData>) => {
      state.trendsData = action.payload;
    },
    clearTrendsData: state => {
      state.trendsData = null;
      state.graphRevision = (state.graphRevision || 0) + 1;
    },
    invalidateGraphCache: state => {
      if (state.trendsData) {
        state.trendsData.overallAnalysisData = null;
      }
      state.graphRevision = (state.graphRevision || 0) + 1;
    },
    setOverallAnalysis: (state, action: PayloadAction<BarData>) => {
      if (state.trendsData) {
        state.trendsData.overallAnalysisData = action.payload;
      }
    },
    setTrendCurveCache: (state, action: PayloadAction<TrendCurveCache>) => {
      if (state.trendsData) {
        state.trendsData.trendCurveCache = action.payload;
      }
    },
    setTrendCurvePreferences: (
      state,
      action: PayloadAction<TrendCurvePreferences>
    ) => {
      if (state.trendsData) {
        state.trendsData.trendCurvePreferences = action.payload;
      }
    },
    setColorAnalysisL1: (state, action: PayloadAction<DataNode>) => {
      if (state.trendsData) {
        state.trendsData.colorAnalysisDataL1 = action.payload;
      }
    },
    setL2Data: (
      state,
      action: PayloadAction<{ color: string; data: DataNode }>
    ) => {
      if (state.trendsData) {
        state.trendsData.l2Data[action.payload.color] = action.payload.data;
      }
    },
    setRequestId: (state, action: PayloadAction<string>) => {
      if (state.trendsData) {
        const prevReqId = state.trendsData.req_id;
        state.trendsData.req_id = action.payload;
        if (state.trendsData.trendCurvePreferences) {
          state.trendsData.trendCurvePreferences.req_id = action.payload;
        }
        // Same req_id (pagination / remount hydrate): keep curve + OA caches.
        // Callers that re-run analysis with an unchanged req_id must bump refresh.
        if (prevReqId === action.payload) {
          return;
        }
        // New analysis/req_id: clear stale archived trends so the new analysis
        // does not inherit the previous one's archived set for curves/OA.
        state.archivedTrends = [];
        state.trendsData.trendCurveCache = null;
        state.trendsData.overallAnalysisData = null;
        state.trendsData.trendCurveRefreshKey =
          (state.trendsData.trendCurveRefreshKey ?? 0) + 1;
      } else {
        state.trendsData = {
          req_id: action.payload,
          overallAnalysisData: null,
          colorAnalysisDataL1: null,
          l2Data: {},
          trendCurveCache: null,
          trendCurvePreferences: null,
          trendCurveRefreshKey: 1,
        };
      }
    },
    bumpTrendCurveRefresh: state => {
      if (state.trendsData) {
        state.trendsData.trendCurveCache = null;
        state.trendsData.trendCurveRefreshKey =
          (state.trendsData.trendCurveRefreshKey ?? 0) + 1;
      }
    },
    setPendingColorForLevel2: (state, action: PayloadAction<string | null>) => {
      state.pendingColorForLevel2 = action.payload;
    },
    setArchivedTrends: (state, action: PayloadAction<string[]>) => {
      state.archivedTrends = action.payload;
    },
  },
});

export const {
  setTrendsData,
  clearTrendsData,
  invalidateGraphCache,
  setOverallAnalysis,
  setTrendCurveCache,
  setTrendCurvePreferences,
  setColorAnalysisL1,
  setL2Data,
  setRequestId,
  bumpTrendCurveRefresh,
  setPendingColorForLevel2,
  setArchivedTrends,
} = trendSlice.actions;

export default trendSlice.reducer;
